/**
 * Where the desktop deck screen's two-pane shell sits, and how tall it is.
 *
 * The shell used to be an ordinary flow box of `calc(100vh - 200px)`. That
 * 200px was never measured against anything — it was written with the two-pane
 * layout and never revisited, while the chrome above the shell (the title
 * block, the study actions, the daily-learning toggle, the search row, plus
 * whichever of the optional rows happen to be showing) is roughly 500px. The
 * shell therefore began around 500 and ended around 1200, so a third of it hung
 * below the window at rest: an empty gap the reader could not fill, a pane
 * whose own scrollbar was not reachable until the page had been scrolled, and a
 * deep-link arrival centring its target inside a box the reader could only see
 * part of. ADR-067 names that shape directly — "a hand-tuned constant standing
 * in for the navigation's rendered height" is in its do-not-reintroduce list,
 * and this was the same defect one element over.
 *
 * The replacement takes the pattern the deck screen's own reference-panel
 * column already uses (`DeckDetailPage`, the `position: sticky` sidebar beside
 * the main column): stick to the viewport, and take a height the viewport
 * actually has. What differs is that nothing here is a literal — both numbers
 * come from the app's one scrolling element (`<main id="app-scroll">`,
 * ADR-067 Decision 3) as it is currently rendered.
 *
 * **The arithmetic, and the invariant that makes it right.** Let `pT`/`pB` be
 * the scrollport's own top and bottom padding and `H` its client height. The
 * shell is `H - pT - pB` tall — the scrollport's visible CONTENT box — and
 * sticks at `top: pT`, which is that content box's top edge. Two consequences,
 * and the second is the one worth stating:
 *
 *  • Stuck, the shell spans exactly the visible content box: its bottom edge is
 *    on screen, above the page's own bottom padding, like every other page.
 *
 *  • The page's scroll range and the shell's sticky travel become the SAME
 *    number, whatever the chrome above measures. The page can scroll by
 *    `pT + chrome + shell + pB - H`, which with this height is exactly
 *    `chrome`; the shell can travel within its containing block (the deck
 *    column, which holds the chrome and the shell and nothing else) by
 *    `(chrome + shell) - shell`, also exactly `chrome`. So the reader scrolls
 *    the chrome away and the page stops at the frame the shell lands stuck —
 *    no overscroll past it, no slack below it, and nothing to re-tune when a
 *    row is added or removed above the shell. `chrome` cancels; it is never
 *    measured, which is why an optional filter bar, selection toolbar or
 *    search-result count cannot put this out of agreement.
 *
 * The floor is the one number here that is chosen rather than measured, and it
 * is not standing in for a rendered height: it is the point below which a card
 * list is not worth showing at all. On a window short enough to hit it the two
 * ranges above stop agreeing and the shell scrolls with the page after its
 * sticky travel is spent — which is the correct outcome, since there is no
 * viewport left to hold it still against.
 */

/** Below this the pane stops being a list. Not a stand-in for anything
 * rendered — see the note above. */
export const PANE_MIN_HEIGHT = 420

/** What the frame reads off the app's scrolling element. */
export interface ScrollportMetrics {
  /** `clientHeight` — the visible padding box, so it already excludes the
   * app header and anything else outside the scroller. */
  clientHeight: number
  paddingTop: number
  paddingBottom: number
}

/**
 * The floor inset, in px: DESIGN_SYSTEM.md §13's chain ends "… + 24 inset".
 * It is bottom padding on each scroll region's own CONTENT — the list's rows
 * and the reading pane — never a shortening of their boxes (§13, "The floor").
 * Both boxes run to the frame's bottom edge; scrolled to the end, the last row
 * and the pane's last line clear that edge by exactly this much; mid-scroll,
 * content is cut by the container's own edge, which is what says more lies
 * below. An inset taken off the boxes instead (2026-09-19, reverted the same
 * day) left a strip of page ground under both columns with rows cut off above
 * it — truncation, not an ending. Applied from this constant in
 * `DeckDesktopPanels`. Chosen, not measured: it stands in for nothing rendered.
 */
export const DECK_FLOOR_INSET = 24

/**
 * The gap between the screen-header bar and the columns' shared head line, in
 * px — the frame's own top padding. The band that used to sit there left for
 * the bar (ADR-067, thirteenth amendment), so the scrollport's 40px top
 * padding would otherwise stand as a strip of page ground above the columns,
 * the same shape of dead band the twelfth amendment removed at the bottom.
 * Equal to the floor inset, so the columns sit in the frame with the same room
 * above their heads as below their last row. Chosen, not measured.
 */
export const DECK_FRAME_TOP_INSET = DECK_FLOOR_INSET

export interface DeckScreenFrame {
  /** The frame's height, in px (border-box: it includes `paddingTop`). */
  height: number
  /** The frame's top margin, in px — always the negative of the scrollport's
   * top padding. See `deckScreenFrame`. */
  marginTop: number
  /** The frame's bottom margin, in px — always the negative of the
   * scrollport's bottom padding. See `deckScreenFrame`. */
  marginBottom: number
  /** The frame's own top padding: `DECK_FRAME_TOP_INSET`. */
  paddingTop: number
}

/**
 * The desktop deck screen's own frame: the two columns, from the bar's bottom
 * edge to the scrollport's bottom edge.
 *
 * **There is no band (2026-09-23, ADR-067 thirteenth amendment).** Until
 * then the frame held a band — identity, study actions, the daily-learning
 * row — above the columns, measured with `useMeasuredHeight` because a band
 * that stays owns its height for the life of the screen. That chrome moved
 * into the screen-header bar, so the columns are the whole frame and nothing
 * in it needs measuring. The history of the band and of the sticky shell before
 * it is in the ADR, not repeated here.
 *
 * **Both scrollport paddings are handed back.** The scrollport's padding exists
 * so a page that scrolls has room at its ends; a frame that never scrolls has
 * no ends, and on it each padding is a strip of page ground belonging to
 * neither column. The bottom one was handed back on 2026-09-19 (twelfth
 * amendment): a negative bottom margin of the same size, so the frame ends on
 * the scrollport's bottom edge and the page's scroll range stays zero. The
 * top one is handed back the same way now, and the frame carries its own
 * `DECK_FRAME_TOP_INSET` as padding instead — so the columns' head line sits a
 * chosen distance under the bar rather than a distance that was the page's.
 * Both paddings are read, never assumed.
 *
 * With both margins the frame's margin box is `clientHeight - pT - pB`, which
 * is exactly the scrollport's content box, so `scrollHeight === clientHeight`.
 *
 * The floor applies to the pane, not to the frame: a window too short to give
 * the columns `minHeight` below the inset produces a frame taller than the
 * scrollport, and the page scrolls by exactly the shortfall — the only case in
 * which this screen scrolls at all, and the correct degradation, since below
 * the floor there is no viewport left to hold anything still against.
 */
export function deckScreenFrame(
  m: ScrollportMetrics,
  minHeight: number = PANE_MIN_HEIGHT,
): DeckScreenFrame {
  return {
    height: Math.max(m.clientHeight, DECK_FRAME_TOP_INSET + minHeight),
    marginTop: -m.paddingTop,
    marginBottom: -m.paddingBottom,
    paddingTop: DECK_FRAME_TOP_INSET,
  }
}
